import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { AdminShell } from '../../components/AdminShell';
import { loadAllShops, mrrFor, colourForShop } from '../../lib/adminQueries';

const fmtMoney = (n) => `£${Number(n || 0).toFixed(0)}`;
const initials = (name) => (name || '').split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
  return `${d.getDate()} ${m}`;
};

const STATUS = (s) => {
  if (!s || s === 'cancelled') return { cls: 'gray', label: 'Not subscribed' };
  if (s === 'active' || s === 'trialing') return { cls: 'green', label: 'Active' };
  if (s === 'past_due') return { cls: 'amber', label: 'Past due — card failed' };
  if (s === 'unpaid')   return { cls: 'amber', label: 'Unpaid' };
  if (s === 'incomplete' || s === 'incomplete_expired') return { cls: 'gray', label: 'Incomplete' };
  return { cls: 'gray', label: s };
};

const AdminBillingInner = () => {
  const [shops, setShops] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const all = await loadAllShops();
      const { data: s } = await supabase.from('platform_settings').select('*').maybeSingle();
      setSettings(s);
      setShops(all);
      setLoading(false);
    })();
  }, []);

  const rows = useMemo(() => shops.map(sh => ({
    ...sh,
    mrr: mrrFor(sh, settings),
  })), [shops, settings]);

  const failed = rows.filter(r => ['past_due', 'unpaid', 'incomplete'].includes(r.subscription_status));
  const totalMrr = rows.reduce((s, r) => s + r.mrr, 0);
  const activeCount = rows.filter(r => ['active','trialing'].includes(r.subscription_status)).length;

  return (
    <main className="main admin-page" data-testid="admin-billing-page">
      <div className="topbar">
        <div>
          <h2>Billing</h2>
          <div className="sub">All subscriptions, statuses, failed payments</div>
        </div>
      </div>

      <div className="body">
        <div className="a-kpis" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 13, marginBottom: 14 }}>
          <div className="kpi" data-testid="admin-billing-kpi-active"><div className="t">Active subscriptions</div><div className="v">{activeCount}</div></div>
          <div className="kpi" data-testid="admin-billing-kpi-mrr"><div className="t">Total MRR</div><div className="v">{fmtMoney(totalMrr)}</div></div>
          <div className="kpi" data-testid="admin-billing-kpi-failed"><div className="t">Failed payments</div><div className="v" style={{ color: failed.length ? 'var(--red)' : 'var(--ink)' }}>{failed.length}</div></div>
        </div>

        <div className="panel" data-testid="admin-billing-table">
          <h3 className="admin-info-header">Subscriptions</h3>
          {loading ? (
            <div style={{ padding: 24 }}>
              {[1,2,3].map(i => <div key={i} className="skel-line" style={{ width: `${60 + i*8}%` }} />)}
            </div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-mute)', fontSize: 'var(--fs-base)' }}>No shops yet.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Shop</th>
                  <th>Plan</th>
                  <th>Billing</th>
                  <th>Interval</th>
                  <th>MRR</th>
                  <th>Next charge</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const sp = STATUS(r.subscription_status);
                  return (
                    <tr key={r.id} className="clickable" onClick={() => window.location.assign(`/admin/shops/${r.id}`)} data-testid={`admin-billing-row-${r.id}`}>
                      <td>
                        <div className="shop">
                          <span className="sq" style={{ background: colourForShop(r) }}>{initials(r.name).charAt(0)}</span>
                          <div>
                            <b>{r.name}</b>
                            <small>{r.subdomain}.instacut.co.uk</small>
                          </div>
                        </div>
                      </td>
                      <td><span className={`pill ${r.plan === 'premium' ? 'prem' : 'gray'}`} style={{ textTransform: 'capitalize' }}>{r.plan}</span></td>
                      <td><span className={`pill ${sp.cls}`}>{sp.label}</span></td>
                      <td style={{ textTransform: 'capitalize' }}>{r.subscription_interval || '—'}</td>
                      <td className="mono">{fmtMoney(r.mrr)}</td>
                      <td className="mono">{fmtDate(r.current_period_end)}</td>
                      <td className="tac">
                        <Link to={`/admin/shops/${r.id}`} className="link" onClick={(e) => e.stopPropagation()}>Open →</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </main>
  );
};

const AdminBilling = () => <AdminShell><AdminBillingInner /></AdminShell>;
export default AdminBilling;
